import { format } from 'date-fns';
import type { EpcScheduleRevisionSummary } from '../../../../services/epcProjectService';

export interface AxisTick {
  label: string;
  pct: number;
}

export interface RevisionLaneVM {
  version: number;
  label: string;
  isBaseline: boolean;
  isCurrent: boolean;
  metaLine: string;
  leftPct: number;
  widthPct: number;
  deltaLabel: string;
  deltaTone: 'bad' | 'good' | 'zero';
  planFinish: string | null;
}

export interface RevisionLanesModel {
  axisStart: Date;
  axisEnd: Date;
  ticks: AxisTick[];
  lanes: RevisionLaneVM[];
  hiddenEarlierCount: number;
}

export interface MilestoneVM {
  name: string;
  finish: string;
}

function parseDay(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function computeAxisRange(
  revisions: Pick<EpcScheduleRevisionSummary, 'planStart' | 'planFinish'>[],
  project: { startDate?: string; commissioningDate?: string },
): { axisStart: Date; axisEnd: Date } | null {
  const starts = [...revisions.map((r) => parseDay(r.planStart)), parseDay(project.startDate)].filter(
    (d): d is Date => d != null,
  );
  const ends = [...revisions.map((r) => parseDay(r.planFinish)), parseDay(project.commissioningDate)].filter(
    (d): d is Date => d != null,
  );
  if (!starts.length || !ends.length) return null;
  const axisStart = new Date(Math.min(...starts.map((d) => d.getTime())));
  const axisEnd = new Date(Math.max(...ends.map((d) => d.getTime())));
  if (axisEnd.getTime() <= axisStart.getTime()) return null;
  return { axisStart, axisEnd };
}

export function pctOnAxis(date: Date, axisStart: Date, axisEnd: Date): number {
  const total = axisEnd.getTime() - axisStart.getTime();
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, ((date.getTime() - axisStart.getTime()) / total) * 100));
}

export function buildAxisTicks(axisStart: Date, axisEnd: Date, count = 7): AxisTick[] {
  const span = axisEnd.getTime() - axisStart.getTime();
  const n = Math.max(2, count);
  const ticks: AxisTick[] = [];
  for (let i = 0; i < n; i += 1) {
    const frac = i / (n - 1);
    ticks.push({ label: format(new Date(axisStart.getTime() + span * frac), 'MMM yy'), pct: frac * 100 });
  }
  return ticks;
}

export function formatDelta(
  rev: Pick<EpcScheduleRevisionSummary, 'isBaseline' | 'deltaFinishDays' | 'planFinish'>,
): { label: string; tone: 'bad' | 'good' | 'zero' } {
  if (rev.isBaseline) {
    const f = parseDay(rev.planFinish);
    return { label: f ? `Finish ${format(f, 'dd MMM yy')}` : 'Finish —', tone: 'zero' };
  }
  if (rev.deltaFinishDays > 0) return { label: `+${rev.deltaFinishDays}d vs baseline`, tone: 'bad' };
  if (rev.deltaFinishDays < 0) return { label: `−${Math.abs(rev.deltaFinishDays)}d vs baseline`, tone: 'good' };
  return { label: '0d', tone: 'zero' };
}

/** Baseline (first) is always shown; then the `maxRecent` most recent. */
export function capLanes<T>(revisions: T[], maxRecent = 5): { visible: T[]; hiddenEarlierCount: number } {
  if (revisions.length <= maxRecent + 1) {
    return { visible: revisions, hiddenEarlierCount: 0 };
  }
  const first = revisions[0] as T;
  const recent = revisions.slice(revisions.length - maxRecent);
  return { visible: [first, ...recent], hiddenEarlierCount: revisions.length - maxRecent - 1 };
}

export function buildRevisionLanes(
  revisions: EpcScheduleRevisionSummary[],
  project: { startDate?: string; commissioningDate?: string },
  maxRecent = 5,
): RevisionLanesModel | null {
  const range = computeAxisRange(revisions, project);
  if (!range) return null;
  const { axisStart, axisEnd } = range;
  const { visible, hiddenEarlierCount } = capLanes(revisions, maxRecent);
  const lanes: RevisionLaneVM[] = visible.map((r) => {
    const s = parseDay(r.planStart) ?? axisStart;
    const f = parseDay(r.planFinish) ?? axisEnd;
    const leftPct = pctOnAxis(s, axisStart, axisEnd);
    const widthPct = Math.max(pctOnAxis(f, axisStart, axisEnd) - leftPct, 1);
    const delta = formatDelta(r);
    return {
      version: r.version,
      label: `R${r.version}`,
      isBaseline: r.isBaseline,
      isCurrent: r.isCurrent,
      metaLine: `Imported ${format(new Date(r.importedAt), 'dd MMM yy')} · ${r.taskCount} tasks`,
      leftPct,
      widthPct,
      deltaLabel: delta.label,
      deltaTone: delta.tone,
      planFinish: r.planFinish,
    };
  });
  return { axisStart, axisEnd, ticks: buildAxisTicks(axisStart, axisEnd), lanes, hiddenEarlierCount };
}

/** Milestone diamonds for the current lane: outline-level-1 summary tasks with finish dates (spec §2.3). */
export function deriveMilestones(tasks: Array<Record<string, unknown>>, max = 6): MilestoneVM[] {
  const out: MilestoneVM[] = [];
  for (const t of tasks) {
    const isSummary = Boolean(t['isSummary'] || t['summary']);
    const level = Number(t['outlineLevel']);
    const finish = typeof t['finish'] === 'string' && t['finish'] ? t['finish'] : null;
    if (!isSummary || level !== 1 || !finish) continue;
    out.push({ name: String(t['name'] ?? ''), finish });
    if (out.length >= max) break;
  }
  return out;
}
